import { useFocusEffect } from 'expo-router';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { RefreshControl, StyleSheet } from 'react-native';
import ConfettiCannon from 'react-native-confetti-cannon';
import Animated, {
    useAnimatedRef,
    useScrollViewOffset,
} from 'react-native-reanimated';
import { H6Regular, isWeb, View, YStack } from 'smoothie';

import TabHeader from '../../common/components/TabHeader';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { SortDirection } from '../../common/types/Common';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import BookGeneralGuidanceWidget from '../../guidanceSessions/components/BookGeneralGuidanceWidget';
import PastSessions from '../../guidanceSessions/components/PastSessions';
import SessionsWidget from '../../guidanceSessions/components/SessionsWidget';
import UpgradePlanWidget from '../../guidanceSessions/components/UpgradePlanWidget';
import { useMembershipGuidanceSessionInfo } from '../../guidanceSessions/hooks/useMembershipGuidanceSessionInfo';
import useSearchGuidanceSessions from '../../guidanceSessions/hooks/useSearchGuidanceSessions';
import { hasRecentlyBookedSessionAtom } from '../../guidanceSessions/state/GuidanceSessionsAtoms';
import {
    GuidanceSessionSortField,
    ScheduledEventStartState,
} from '../../guidanceSessions/types/GuidanceSessions';
import MessagingSnapshot from '../../guideMessaging/components/MessagingSnapshot';
import { MessagingSnapshotContext } from '../../guideMessaging/types/GuideMessaging';
import GuideOverview from './GuideOverview';

const MyGuide = () => {
    useTrackViewOnFocus('MyGuide');
    const user = useAuthedUser();
    const {
        canBookGuidanceGeneralSession,
        canUpgradeForMoreGuidanceSessions,
        hasCompletedGuidanceOnboarding,
    } = useMembershipGuidanceSessionInfo();

    const { id: userId, membership } = user ?? {};
    const { id: membershipId, guide } = membership ?? {};

    const scrollRef = useAnimatedRef<Animated.ScrollView>();
    const scrollOffsetRef = useScrollViewOffset(scrollRef);
    const [shouldShowConfetti, setShouldShowConfetti] = useState(false);
    const [hasRecentlyBookedSession, setHasRecentlyBookedSession] = useAtom(
        hasRecentlyBookedSessionAtom,
    );

    const {
        data: upcomingSessions,
        isLoading: isLoadingUpcomingSessions,
        isFetching: isFetchingUpcomingSessions,
        refetch: refetchUpcomingSessions,
    } = useSearchGuidanceSessions(
        {
            userId: userId ?? '',
            membershipId: membershipId ?? '',
            state: ScheduledEventStartState.UPCOMING,
            sortField: GuidanceSessionSortField.START_TIME,
            sortDirection: SortDirection.ASC,
        },
        {
            enabled: membershipId != null && userId != null,
            refetchOnWindowFocus: true,
        },
    );

    const {
        data: pastSessions,
        isLoading: isLoadingPastSessions,
        isFetching: isFetchingPastSessions,
        refetch: refetchPastSessions,
    } = useSearchGuidanceSessions(
        {
            userId: userId ?? '',
            membershipId: membershipId ?? '',
            state: ScheduledEventStartState.PAST,
            sortField: GuidanceSessionSortField.START_TIME,
            sortDirection: SortDirection.DESC,
        },
        {
            enabled: membershipId != null && userId != null,
            refetchOnWindowFocus: true,
        },
    );
    const pastSessionsList = pastSessions?.results ?? [];

    const isLoadingSessions =
        isLoadingPastSessions ||
        isLoadingUpcomingSessions ||
        isFetchingPastSessions ||
        isFetchingUpcomingSessions;

    // Show SessionsWidget if the user has an upcoming session or has not completed
    // guidance onboarding. Otherwise, show BookGeneralGuidanceSessionCard.
    const hasUpcomingSession = (upcomingSessions?.results.length ?? 0) > 0;
    const completedGuidanceOnboarding =
        hasCompletedGuidanceOnboarding(pastSessionsList);
    const shouldShowSessionsWidget =
        hasUpcomingSession || !completedGuidanceOnboarding;
    const shouldShowBookGeneralGuidanceSessionCard =
        !shouldShowSessionsWidget &&
        canBookGuidanceGeneralSession(pastSessionsList);
    const shouldShowUpgradePlanCard =
        !shouldShowSessionsWidget &&
        canUpgradeForMoreGuidanceSessions(pastSessionsList);
    const shouldShowGuideOverview = guide != null;
    const shouldShowMessagingSnapshot = guide != null;

    const onRefresh = () => {
        void refetchUpcomingSessions();
        void refetchPastSessions();
    };

    // On screen (re-)focus, trigger any UI effects.
    useFocusEffect(() => {
        if (hasRecentlyBookedSession) {
            scrollRef.current?.scrollTo({
                y: 0,
                animated: true,
            });

            setShouldShowConfetti(true);
        }
    });

    return (
        <View flex={1}>
            <TabHeader headerTitle="Guidance" />

            <Animated.ScrollView
                ref={scrollRef}
                showsVerticalScrollIndicator={isWeb}
                refreshControl={
                    <RefreshControl
                        refreshing={isLoadingSessions}
                        onRefresh={onRefresh}
                    />
                }
                scrollEventThrottle={16}
            >
                <YStack
                    gap={24}
                    paddingHorizontal={24}
                    marginBottom={50}
                    $sm={{
                        paddingHorizontal: 36,
                        gap: 36,
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                    }}
                    $lg={{
                        gap: 48,
                    }}
                    width="100%"
                    maxWidth={1140}
                    alignSelf="center"
                >
                    {shouldShowGuideOverview && (
                        <View
                            $sm={{
                                ...styles.flexProps,
                            }}
                        >
                            <GuideOverview
                                guide={guide}
                                membership={membership}
                            />
                        </View>
                    )}
                    {shouldShowSessionsWidget && (
                        <View
                            $sm={{
                                ...styles.flexProps,
                            }}
                        >
                            <SessionsWidget
                                isLoading={isLoadingSessions}
                                upcomingSessions={upcomingSessions}
                                pastSessions={pastSessions}
                                hasCompletedGuidanceOnboarding={
                                    completedGuidanceOnboarding
                                }
                            />
                        </View>
                    )}
                    {shouldShowMessagingSnapshot && (
                        <View
                            $sm={{
                                ...styles.flexProps,
                                gap: 24,
                            }}
                        >
                            <View
                                $sm={{
                                    flex: 1,
                                }}
                            >
                                <H6Regular marginBottom={12}>
                                    Messages
                                </H6Regular>
                                <MessagingSnapshot
                                    guide={guide}
                                    context={MessagingSnapshotContext.MY_GUIDE}
                                    scrollOffset={scrollOffsetRef}
                                />
                            </View>
                        </View>
                    )}
                    {shouldShowBookGeneralGuidanceSessionCard && (
                        <View
                            $sm={{
                                ...styles.flexProps,
                            }}
                        >
                            <BookGeneralGuidanceWidget />
                        </View>
                    )}
                    {shouldShowUpgradePlanCard && (
                        <View
                            $sm={{
                                ...styles.flexProps,
                            }}
                        >
                            <UpgradePlanWidget />
                        </View>
                    )}
                    <View
                        $sm={{
                            ...styles.flexProps,
                        }}
                    >
                        <PastSessions
                            isLoading={
                                isLoadingPastSessions || isFetchingPastSessions
                            }
                            sessions={pastSessions}
                            guide={guide}
                        />
                    </View>
                </YStack>
            </Animated.ScrollView>

            {shouldShowConfetti && (
                <ConfettiCannon
                    count={50}
                    origin={{ x: 0, y: 0 }}
                    fadeOut={true}
                    fallSpeed={2000}
                    onAnimationEnd={() => {
                        setHasRecentlyBookedSession(false);
                        setShouldShowConfetti(false);
                    }}
                />
            )}
        </View>
    );
};

export default MyGuide;

const styles = StyleSheet.create({
    flexProps: {
        flex: 0.5,
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: '45%',
        maxWidth: '48%',
    },
});
